import React, { ReactElement, useRef, useState, useEffect, useMemo } from "react";
import { useLocalization } from "@progress/kendo-react-intl";
import { enMessages } from "../../../../messages/en-US";
import {
  Grid,
  GridCellProps,
  GridColumn,
  GridDataStateChangeEvent,
  GridToolbar,
} from "@progress/kendo-react-grid";
import {
  DataResult,
  SortDescriptor,
  State,
  process,
} from "@progress/kendo-data-query";
import { ExcelExport, ExcelExportColumn } from "@progress/kendo-react-excel-export";
import { Button } from "@progress/kendo-react-buttons";
import { downloadIcon, pencilIcon, plusIcon, trashIcon } from "@progress/kendo-svg-icons";
import AssetTemplateDialog from "../Dialogs/AssetTemplateDialog";
import { getTemplateTypeDisplay } from "../Dialogs/assetTemplateDialogUtils";
import GenericConfirmationDialog from "../../../Shared/Components/Dialogs/GenericConfirmationDialog";
import templatesApiService from "../../Utilities/TemplatesApiService";
import AssetTemplateListItemDto from "../../../../Models/Assets/AssetTemplateListItemDto";
import useIdentityStore from "../../../../Zustand/identityStore";

export interface AssetTemplateTableProps {
  height: number;
  data?: AssetTemplateListItemDto[];
  onTemplateSaved?: () => void;
  onTemplateDeleted?: () => void;
  /** When true (e.g. from Account Setup), Add shows choice: new template or copy from existing. */
  showTemplateSourceChoice?: boolean;
  /** When true (e.g. from Administration), templates are for all sites (siteId ""), account column/field hidden. */
  isAdministrationMode?: boolean;
}

export default function AssetTemplateTable(props: AssetTemplateTableProps): ReactElement {
  const localizationService = useLocalization();
  const currentUserRole = useIdentityStore((x) => x.user?.profile?.role);
  const canAddEditDelete = currentUserRole !== "GeneralUser";
  const data = useMemo(() => {
    const rawData = props.data || [];
    return props.isAdministrationMode
      ? rawData.filter((item) => !item.siteId || item.siteId.trim() === "")
      : rawData;
  }, [props.data, props.isAdministrationMode]);

  const initialSort: Array<SortDescriptor> = [{ field: "name", dir: "asc" }];
  const [sort] = useState(initialSort);

  const _export = useRef<ExcelExport | null>(null);
  const excelExport = () => {
    if (_export.current !== null) {
      _export.current.save();
    }
  };

  const createDataState = (dataState: State) => {
    return {
      result: process(data.slice(0), dataState),
      dataState: dataState,
    };
  };

  const initialState = createDataState({
    take: 30,
    skip: 0,
    sort: sort,
  });

  const [result, setResult] = useState<DataResult>(initialState.result);
  const [dataState, setDataState] = useState<State>(initialState.dataState);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedAssetTemplate, setSelectedAssetTemplate] = useState<any>(null);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<{ id: string; name?: string } | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const dataStateChange = (event: GridDataStateChangeEvent) => {
    const updatedState = createDataState(event.dataState);
    setResult(updatedState.result);
    setDataState(updatedState.dataState);
  };

  useEffect(() => {
    const updatedState = createDataState(dataState);
    setResult(updatedState.result);
    setDataState(updatedState.dataState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const getExcelExportData = () => {
    return process(data, {
      filter: dataState.filter,
      sort: dataState.sort,
    }).data;
  };

  return (
    <div>
      <ExcelExport
        data={getExcelExportData()}
        ref={_export}
        collapsible={true}
        fileName="AssetTemplates"
      >
        <ExcelExportColumn
          field="name"
          title={localizationService.toLanguageString("custom.name", enMessages.custom.name)}
        />
      </ExcelExport>

      <Grid
        key="AssetTemplateTable"
        data={result}
        scrollable="virtual"
        take={30}
        rowHeight={46}
        onDataStateChange={dataStateChange}
        {...dataState}
        sortable={{ mode: "multiple" }}
        filterable={true}
        style={{ height: props.height }}
      >
        <GridToolbar>
          <div className="w-full flex justify-between items-center">
            <div className="gb-h3">Templates</div>
            <div className="flex gap-2">
              {canAddEditDelete && (
                <Button
                  title={localizationService.toLanguageString("custom.add", enMessages.custom.add)}
                  onClick={() => {
                    setSelectedAssetTemplate(null);
                    setIsDialogOpen(true);
                  }}
                  themeColor="primary"
                  svgIcon={plusIcon}
                >
                  {localizationService.toLanguageString("custom.add", enMessages.custom.add)} Template
                </Button>
              )}
              {canAddEditDelete && (
                <Button
                  title={localizationService.toLanguageString(
                    "custom.exportToExcel",
                    enMessages.custom.exportToExcel
                  )}
                  onClick={excelExport}
                  themeColor="base"
                  svgIcon={downloadIcon}
                  style={{ marginRight: "10px" }}
                />
              )}
            </div>
          </div>
        </GridToolbar>

        <GridColumn
          field="name"
          title={localizationService.toLanguageString("custom.name", enMessages.custom.name)}
        />
        <GridColumn
          field="type"
          title={localizationService.toLanguageString("custom.type", enMessages.custom.type)}
          cell={(cellProps: GridCellProps) => (
            <td>
              <span>
                {getTemplateTypeDisplay((cellProps.dataItem as any)?.type)}
              </span>
            </td>
          )}
        />
        <GridColumn
          field="hasGreasePumps"
          title={localizationService.toLanguageString(
            "custom.greasePumpConnectivity",
            enMessages.custom.greasePumpConnectivity
          )}
          cell={(cellProps: GridCellProps) => (
            <td>
              <span>
                {cellProps.dataItem.hasGreasePumps
                  ? localizationService.toLanguageString("custom.enabled", enMessages.custom.enabled)
                  : localizationService.toLanguageString("custom.disabled", enMessages.custom.disabled)}
              </span>
            </td>
          )}
        />
        {!props.isAdministrationMode && (
          <GridColumn
            field="assetCount"
            title={localizationService.toLanguageString(
              "custom.assetCount",
              enMessages.custom.assetCount
            )}
            cell={(cellProps: GridCellProps) => (
              <td>
                <span>{(cellProps.dataItem as AssetTemplateListItemDto)?.assetCount ?? 0}</span>
              </td>
            )}
          />
        )}
        {!props.showTemplateSourceChoice && !props.isAdministrationMode && (
          <GridColumn
            field="accountName"
            title={localizationService.toLanguageString("custom.account", enMessages.custom.account)}
            cell={(cellProps: GridCellProps) => {
              const item = cellProps.dataItem as AssetTemplateListItemDto;
              return (
                <td>
                  {item?.accountName?.trim() ? item.accountName : "All Accounts"}
                </td>
              );
            }}
          />
        )}
        {canAddEditDelete && (
          <GridColumn
            field="actions"
            title={localizationService.toLanguageString("custom.actions", enMessages.custom.actions)}
            width="110px"
            sortable={false}
            filterable={false}
            cell={(cellProps: GridCellProps) => {
              const rowId = (cellProps.dataItem as any)?.id;
              const handleEdit = () => {
                if (!rowId) return;
                setSelectedAssetTemplate({ id: rowId });
                setIsDialogOpen(true);
                setIsLoadingTemplate(true);
                templatesApiService
                  .getTemplateAsset(rowId)
                  .then((template) => {
                    setSelectedAssetTemplate(template);
                    setIsLoadingTemplate(false);
                  })
                  .catch((err) => {
                    console.error("Error loading template for edit:", err);
                    setIsLoadingTemplate(false);
                    setIsDialogOpen(false);
                    setSelectedAssetTemplate(null);
                  });
              };
              const handleDelete = () => {
                setTemplateToDelete({
                  id: rowId,
                  name: (cellProps.dataItem as any)?.name,
                });
                setIsDeleteDialogOpen(true);
              };
              const item = cellProps.dataItem as AssetTemplateListItemDto;
              const canDelete = props.isAdministrationMode
                ? true
                : (item?.assetCount ?? 0) === 0;
              return (
                <td style={{ width: 110, maxWidth: 110 }}>
                  <div className="flex items-center justify-center gap-1">
                    <Button
                      themeColor="primary"
                      svgIcon={pencilIcon}
                      title={localizationService.toLanguageString("custom.edit", enMessages.custom.edit)}
                      onClick={handleEdit}
                    />
                    <Button
                      themeColor="primary"
                      svgIcon={trashIcon}
                      disabled={!canDelete}
                      title={localizationService.toLanguageString(
                        "custom.delete",
                        enMessages.custom.delete
                      )}
                      onClick={handleDelete}
                    />
                  </div>
                </td>
              );
            }}
          />
        )}
      </Grid>

      {isDialogOpen && (
        <AssetTemplateDialog
          onClose={() => {
            setIsDialogOpen(false);
            setSelectedAssetTemplate(null);
            setIsLoadingTemplate(false);
          }}
          onSuccess={props.onTemplateSaved}
          assetTemplate={selectedAssetTemplate}
          existingTemplates={data}
          isLoadingTemplate={isLoadingTemplate}
          showTemplateSourceChoice={props.showTemplateSourceChoice}
          isAdministrationMode={props.isAdministrationMode}
        />
      )}

      <GenericConfirmationDialog
        open={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false);
          setTemplateToDelete(null);
        }}
        header={
          localizationService.toLanguageString("custom.delete", enMessages.custom.delete) +
          " Template"
        }
        body={
          templateToDelete?.name
            ? `${templateToDelete.name} ${localizationService.toLanguageString("custom.deleteItemLabel", enMessages.custom.deleteItemLabel)}`
            : localizationService.toLanguageString(
                "custom.deleteItemsConfirmation",
                enMessages.custom.deleteItemsConfirmation
              )
        }
        cancel={localizationService.toLanguageString("custom.cancel", enMessages.custom.cancel)}
        confirm={localizationService.toLanguageString("custom.delete", enMessages.custom.delete)}
        confirmButtonTheme="primary"
        onConfirm={async () => {
          if (!templateToDelete?.id) return;
          try {
            await templatesApiService.deleteTemplateAsset(templateToDelete.id);
            props.onTemplateDeleted?.();
          } catch (err) {
            console.error("Error deleting template:", err);
          } finally {
            setIsDeleteDialogOpen(false);
            setTemplateToDelete(null);
          }
        }}
      />
    </div>
  );
}
